/**
 * Filter Preset Service
 *
 * Handles CRUD operations for filter presets stored in localStorage.
 * Presets persist date range, granularity, and comparison mode settings.
 *
 * Storage key: 'monutrack:filter-presets'
 * Max presets: 10 (FIFO deletion when exceeded)
 */

import type { DREGranularity } from '../components/DREControlsPanel';
import type { ComparisonMode } from '../components/ComparisonSelector';

// ============================================================================
// TYPES
// ============================================================================

export interface FilterPresetFilters {
  startDate: string; // 'YYYY-MM-DD'
  endDate: string; // 'YYYY-MM-DD'
  granularity: DREGranularity;
  comparisonMode: ComparisonMode;
}

export interface FilterPreset {
  id: string;
  name: string;
  createdAt: string; // ISO date string for FIFO ordering
  filters: FilterPresetFilters;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const PRESET_STORAGE_KEY = 'monutrack:filter-presets';
export const MAX_PRESETS = 10;

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Get all saved presets from localStorage
 */
export function getPresets(): FilterPreset[] {
  try {
    const stored = localStorage.getItem(PRESET_STORAGE_KEY);
    if (!stored) return [];

    const presets = JSON.parse(stored) as FilterPreset[];
    // Sort by createdAt ascending (oldest first)
    return presets.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  } catch {
    console.error('[filterPresetService] Failed to parse presets from localStorage');
    return [];
  }
}

/**
 * Save a new preset or update an existing one by name
 * Returns the saved preset
 */
export function savePreset(name: string, filters: FilterPresetFilters): FilterPreset {
  const presets = getPresets();
  const trimmedName = name.trim();

  // Check for existing preset with same name
  const existingIndex = presets.findIndex((p) => p.name === trimmedName);

  if (existingIndex >= 0) {
    // Update existing preset
    const updatedPreset: FilterPreset = {
      ...presets[existingIndex],
      filters,
      createdAt: new Date().toISOString(), // Update timestamp to make it "newest"
    };
    presets[existingIndex] = updatedPreset;
    persistPresets(presets);
    return updatedPreset;
  }

  // Create new preset
  const newPreset: FilterPreset = {
    id: generateId(),
    name: trimmedName,
    createdAt: new Date().toISOString(),
    filters,
  };

  // FIFO: Remove oldest preset(s) if we're at max capacity
  while (presets.length >= MAX_PRESETS) {
    presets.shift(); // Remove oldest (first element since sorted by createdAt)
  }

  presets.push(newPreset);
  persistPresets(presets);

  return newPreset;
}

/**
 * Delete a preset by ID
 * Returns true if deleted, false if not found
 */
export function deletePreset(presetId: string): boolean {
  const presets = getPresets();
  const initialLength = presets.length;

  const filtered = presets.filter((p) => p.id !== presetId);

  if (filtered.length === initialLength) {
    return false; // Not found
  }

  persistPresets(filtered);
  return true;
}

/**
 * Update an existing preset
 * Returns the updated preset or null if not found
 */
export function updatePreset(
  presetId: string,
  updates: Partial<Pick<FilterPreset, 'name' | 'filters'>>
): FilterPreset | null {
  const presets = getPresets();
  const index = presets.findIndex((p) => p.id === presetId);

  if (index < 0) {
    return null;
  }

  const updatedPreset: FilterPreset = {
    ...presets[index],
    ...(updates.name && { name: updates.name.trim() }),
    ...(updates.filters && { filters: updates.filters }),
  };

  presets[index] = updatedPreset;
  persistPresets(presets);

  return updatedPreset;
}

/**
 * Check if a preset name already exists
 */
export function presetNameExists(name: string): boolean {
  const presets = getPresets();
  return presets.some((p) => p.name.toLowerCase() === name.trim().toLowerCase());
}

/**
 * Get a preset by ID
 */
export function getPresetById(presetId: string): FilterPreset | null {
  const presets = getPresets();
  return presets.find((p) => p.id === presetId) ?? null;
}

// ============================================================================
// HELPERS
// ============================================================================

function persistPresets(presets: FilterPreset[]): void {
  try {
    localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(presets));
  } catch (error) {
    console.error('[filterPresetService] Failed to persist presets:', error);
  }
}

function generateId(): string {
  // Use crypto.randomUUID if available, fallback to timestamp-based ID
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `preset-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
